import React, { useState, useEffect } from 'react';
import { FormInput, FormSelect, SearchableSelect, Modal, FormActionButtons } from '@/components/ui';
import toast from 'react-hot-toast';
import api from '@/services/api';

export const CreditPaymentForm = ({ isOpen, onClose, onConfirm }) => {
    const [customers, setCustomers] = useState([]);
    
    const [form, setForm] = useState({
        relatedProjectId: '',
        relatedProjectCollection: 'SDContact',
        stage: 'advance',
        paymentDate: new Date().toISOString().split('T')[0],
        amount: '',
        method: 'cheque',
        transactionId: '',
        bankName: '',
        branch: '',
        upiPhone: '',
        receiverName: '',
        receiverPhone: '',
        chequeNumber: '',
        chequeDate: '',
        remarks: ''
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setForm({
                relatedProjectId: '',
                relatedProjectCollection: 'SDContact',
                stage: 'advance',
                paymentDate: new Date().toISOString().split('T')[0],
                amount: '',
                method: 'cheque',
                transactionId: '',
                bankName: '',
                branch: '',
                upiPhone: '',
                receiverName: '',
                receiverPhone: '',
                chequeNumber: '',
                chequeDate: '',
                remarks: ''
            });
            
            // Fetch customers
            api.get('/customers')
                .then(res => {
                    let cList = res.data || [];
                    if (!Array.isArray(cList) && res.data && Array.isArray(res.data.customers)) {
                        cList = res.data.customers;
                    }
                    setCustomers(cList);
                })
                .catch(err => console.error("Failed to fetch customers", err));
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const payAmount = Number(form.amount);
        if (!form.relatedProjectId) return toast.error("Please select a Customer");
        if (!payAmount || payAmount <= 0) return toast.error("Please enter a valid payment amount");
        if (!form.stage) return toast.error("Please select a payment type");
        if (!form.paymentDate) return toast.error("Please enter a transaction date");

        setLoading(true);
        try {
            if (onConfirm) {
                await onConfirm(form);
            } else {
                toast.success("Received amount recorded successfully!");
                onClose();
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to record payment");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const selectedCustomer = customers.find(c => c._id === form.relatedProjectId);
    const customerNameDisplay = selectedCustomer ? (selectedCustomer.customerName || selectedCustomer.name || 'Customer') : 'Customer';
    const customerIdDisplay = selectedCustomer ? (selectedCustomer.sdId || selectedCustomer.customId || '') : '';
    const headerContext = selectedCustomer ? `${customerNameDisplay} ${customerIdDisplay ? `• ${customerIdDisplay}` : ''}` : 'New Payment';

    return (
        <Modal 
          isOpen={isOpen} 
          onClose={onClose} 
          size="max-w-2xl"
          title={
            <div className="flex flex-col gap-0.5 z-10 relative">
              <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
                SPACE DESIGNS
              </span>
              <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
                Received Amount Entry
              </span>
              <span className="text-[10px] text-slate-100 mt-1">
                {headerContext}
              </span>
            </div>
          }
        >
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <SearchableSelect
                    label="Select Customer *"
                    placeholder="Search Customer / SD ID..."
                    value={form.relatedProjectId}
                    options={customers.map(p => ({
                        label: p.customerName || p.name || "Unknown Customer",
                        dropdownLabel: `${p.customerName || p.name} — ${p.sdId || p.customId || 'N/A'}`,
                        value: p._id,
                        model: 'SDContact'
                    }))}
                    onChange={(val) => {
                        setForm(prev => ({ ...prev, relatedProjectId: val }));
                    }}
                />
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Transaction Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={form.paymentDate}
                    onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Amount Received (₹) <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-slate-400 font-semibold text-sm">₹</span>
                    <input
                      type="number"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      className="w-full h-9 pl-8 pr-3 rounded-md border border-slate-300 text-slate-900 text-xs font-bold outline-none focus:border-slate-500"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
                <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-1">Payment type <span className="text-red-500">*</span></label>
                    <select 
                        value={form.stage}
                        onChange={(e) => setForm({ ...form, stage: e.target.value })}
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                    >
                        <option value="advance">Advance</option>
                        <option value="balance">Balance</option>
                        <option value="other">Other</option>
                    </select>
                </div>
              </div>

              {/* Payment Method Selector Tabs */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Select Payment Method</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, method: 'bank' })}
                    className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${form.method === 'bank' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                  >
                    🏦 Bank
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, method: 'upi' })}
                    className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${form.method === 'upi' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                  >
                    📱 UPI
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, method: 'cash' })}
                    className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${form.method === 'cash' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                  >
                    💵 Cash
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, method: 'cheque' })}
                    className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${form.method === 'cheque' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                  >
                    🧾 Cheque
                  </button>
                </div>
              </div>

              {/* Dynamic Payment Method Fields */}
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200/80 space-y-2">
                {form.method === 'cheque' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Cheque Number <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.chequeNumber}
                        onChange={(e) => setForm({ ...form, chequeNumber: e.target.value })}
                        placeholder="6-digit No."
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Cheque Date <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={form.chequeDate}
                        onChange={(e) => setForm({ ...form, chequeDate: e.target.value })}
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Issuing Bank <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.bankName}
                        onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                        placeholder="Bank name"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                  </div>
                )}

                {form.method === 'upi' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">UPI UTR / Ref No. <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.transactionId}
                        onChange={(e) => setForm({ ...form, transactionId: e.target.value })}
                        placeholder="TRX-123..."
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">UPI Phone Number <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        pattern="[0-9]{10}"
                        maxLength={10}
                        value={form.upiPhone}
                        onChange={(e) => setForm({ ...form, upiPhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        placeholder="10-digit number"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                  </div>
                )}

                {form.method === 'cash' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Receiver Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.receiverName}
                        onChange={(e) => setForm({ ...form, receiverName: e.target.value })}
                        placeholder="Who received"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Receiver Phone <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        pattern="[0-9]{10}"
                        maxLength={10}
                        value={form.receiverPhone}
                        onChange={(e) => setForm({ ...form, receiverPhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        placeholder="10-digit number"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                  </div>
                )}

                {form.method === 'bank' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Transaction / Ref ID <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.transactionId}
                        onChange={(e) => setForm({ ...form, transactionId: e.target.value })}
                        placeholder="TRX-123..."
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Bank Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.bankName}
                        onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                        placeholder="Bank name"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Branch</label>
                      <input
                        type="text"
                        value={form.branch}
                        onChange={(e) => setForm({ ...form, branch: e.target.value })}
                        placeholder="Branch Name"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                      />
                    </div>
                  </div>
                )}
                
                <div className="col-span-2 pt-1">
                    <label className="block text-[11px] font-medium text-slate-600 mb-1">Internal Remarks</label>
                    <input
                        type="text"
                        value={form.remarks}
                        onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                        placeholder="Optional notes..."
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                    />
                </div>
              </div>

              {/* Submit Buttons */}
              <FormActionButtons 
                onClose={onClose}
                cancelText="Discard"
                submitText="Record Payout"
                isLoading={loading}
              />
            </form>
        </Modal>
    );
};
